import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import ProfileHeader from '../components/ProfileHeader';
import {
  cancelOrganizationSubscription,
  createSubscriptionPlan,
  getOrganizationCommercialState,
  normalizeOrganizationSubscription,
  normalizeSubscriptionPlan,
  setOrganizationPlan,
  startOrganizationTrial,
  syncOrganizationSubscriptionEntitlements,
} from '../utils/billing';
import { normalizeContentPackRecord, normalizeEntitlementRecord } from '../utils/contentPackAccess';
import { canManageSubscriptionPlans } from '../utils/platformAccess';

function PlatformAdminBillingPage() {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contentPacks, setContentPacks] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [planForm, setPlanForm] = useState({
    code: '',
    name: '',
    tier: 'free',
    billingInterval: 'none',
    basePrice: '0',
    currency: 'USD',
    includedInstructorSeats: '0',
    includedContentPackIdsText: '',
    addOnEligibleContentPackIdsText: '',
  });
  const [subscriptionForm, setSubscriptionForm] = useState({
    organizationId: '',
    planCode: '',
    billingInterval: 'monthly',
    seatCount: '0',
    instructorSeatLimit: '0',
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          planSnap,
          subscriptionSnap,
          organizationSnap,
          contentPackSnap,
          entitlementSnap,
        ] = await Promise.all([
          getDocs(collection(db, 'subscriptionPlans')),
          getDocs(collection(db, 'organizationSubscriptions')),
          getDocs(collection(db, 'organizations')),
          getDocs(collection(db, 'contentPacks')),
          getDocs(collection(db, 'entitlements')),
        ]);

        setPlans(
          (planSnap?.docs || [])
            .map((docSnap) => normalizeSubscriptionPlan({ id: docSnap.id, ...docSnap.data() }))
            .filter(Boolean)
        );
        setSubscriptions(
          (subscriptionSnap?.docs || [])
            .map((docSnap) =>
              normalizeOrganizationSubscription({ id: docSnap.id, ...docSnap.data() })
            )
            .filter(Boolean)
        );
        setOrganizations((organizationSnap?.docs || []).map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setContentPacks(
          (contentPackSnap?.docs || []).map((docSnap) =>
            normalizeContentPackRecord({ id: docSnap.id, ...docSnap.data() })
          )
        );
        setEntitlements(
          (entitlementSnap?.docs || []).map((docSnap) =>
            normalizeEntitlementRecord({ id: docSnap.id, ...docSnap.data() })
          )
        );
      } catch (loadError) {
        console.error(loadError);
        setError('Could not load billing data right now.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const editable = canManageSubscriptionPlans(userData);

  const organizationCommercialRows = useMemo(
    () =>
      organizations.map((organization) => {
        const subscription =
          (subscriptions || []).find((item) => item?.organizationId === organization.id) || null;
        return {
          organization,
          commercialState: getOrganizationCommercialState(organization.id, {
            subscription,
            subscriptionPlans: plans,
            entitlements,
            contentPacks,
          }),
        };
      }),
    [contentPacks, entitlements, organizations, plans, subscriptions]
  );
  const pendingCommercialRequests = useMemo(
    () =>
      organizations.filter((organization) => organization?.commercialRequestStatus === 'pending'),
    [organizations]
  );

  const handleCreatePlan = async () => {
    try {
      await createSubscriptionPlan(db, {
        code: planForm.code,
        name: planForm.name,
        tier: planForm.tier,
        billingInterval: planForm.billingInterval,
        basePrice: Number(planForm.basePrice || 0),
        currency: planForm.currency,
        includedInstructorSeats: Number(planForm.includedInstructorSeats || 0),
        includedContentPackIds: planForm.includedContentPackIdsText.split(',').map((item) => item.trim()).filter(Boolean),
        addOnEligibleContentPackIds: planForm.addOnEligibleContentPackIdsText.split(',').map((item) => item.trim()).filter(Boolean),
      });
      setMessage('Subscription plan created.');
      setError('');
    } catch (createError) {
      console.error(createError);
      setError('Could not create the subscription plan.');
    }
  };

  const handleUpdatePlanActive = async (planId, active) => {
    try {
      await updateDoc(doc(db, 'subscriptionPlans', planId), {
        active,
      });
      setPlans((current) =>
        current.map((plan) =>
          plan.subscriptionPlanId === planId ? { ...plan, active } : plan
        )
      );
      setMessage('Plan updated.');
      setError('');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not update the plan.');
    }
  };

  const handleAssignPlan = async () => {
    try {
      const selectedPlan = plans.find((plan) => plan.code === subscriptionForm.planCode);
      const nextSubscription = await setOrganizationPlan(db, {
        organizationId: subscriptionForm.organizationId,
        planCode: subscriptionForm.planCode,
        subscriptionPlanId: selectedPlan?.subscriptionPlanId || '',
        billingInterval: subscriptionForm.billingInterval,
        seatCount: Number(subscriptionForm.seatCount || 0),
        instructorSeatLimit:
          Number(subscriptionForm.instructorSeatLimit || 0) ||
          Number(selectedPlan?.includedInstructorSeats || 0),
        currentPeriodStartAt: new Date().toISOString(),
      });

      await syncOrganizationSubscriptionEntitlements(db, {
        organizationId: subscriptionForm.organizationId,
        subscription: nextSubscription,
        plans,
        existingEntitlements: entitlements,
        grantedByUserId: userData?.uid || '',
      });

      setMessage('Organization plan assigned.');
      setError('');
    } catch (assignError) {
      console.error(assignError);
      setError('Could not assign the organization plan.');
    }
  };

  const handleStartTrial = async (organizationId, planCode) => {
    try {
      const selectedPlan = plans.find((plan) => plan.code === planCode);
      const nextSubscription = await startOrganizationTrial(db, {
        organizationId,
        planCode,
        subscriptionPlanId: selectedPlan?.subscriptionPlanId || '',
        trialStartAt: new Date().toISOString(),
        trialEndAt: new Date(Date.now() + (14 * 24 * 60 * 60 * 1000)).toISOString(),
        instructorSeatLimit: Number(selectedPlan?.includedInstructorSeats || 0),
      });

      await syncOrganizationSubscriptionEntitlements(db, {
        organizationId,
        subscription: nextSubscription,
        plans,
        existingEntitlements: entitlements,
        grantedByUserId: userData?.uid || '',
      });

      setMessage('Organization trial started.');
      setError('');
    } catch (trialError) {
      console.error(trialError);
      setError('Could not start the organization trial.');
    }
  };

  const handleCancelSubscription = async (organizationId) => {
    try {
      await cancelOrganizationSubscription(db, {
        organizationId,
      });
      setSubscriptions((current) =>
        current.map((subscription) =>
          subscription.organizationId === organizationId
            ? { ...subscription, status: 'canceled', cancelAtPeriodEnd: true }
            : subscription
        )
      );
      setMessage('Organization subscription canceled.');
      setError('');
    } catch (cancelError) {
      console.error(cancelError);
      setError('Could not cancel the organization subscription.');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-8">
      <ProfileHeader />
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Platform Billing</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">Commercial Management</h1>
        <p className="mt-2 text-sm text-slate-600">
          Manage plans, organization subscriptions, trials, and commercial access sources without turning on payment processing yet.
        </p>
        {message ? <p className="mt-4 rounded bg-green-50 px-3 py-2 text-sm text-green-700">{message}</p> : null}
        {error ? <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
      </section>

      {loading ? (
        <p>Loading billing workspace...</p>
      ) : (
        <>
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Subscription Plans</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input value={planForm.code} onChange={(e) => setPlanForm((current) => ({ ...current, code: e.target.value }))} placeholder="Plan code" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.name} onChange={(e) => setPlanForm((current) => ({ ...current, name: e.target.value }))} placeholder="Plan name" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.tier} onChange={(e) => setPlanForm((current) => ({ ...current, tier: e.target.value }))} placeholder="Tier" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <select value={planForm.billingInterval} onChange={(e) => setPlanForm((current) => ({ ...current, billingInterval: e.target.value }))} className="rounded border border-slate-200 px-3 py-2 text-sm">
                <option value="none">None</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
              <input value={planForm.basePrice} onChange={(e) => setPlanForm((current) => ({ ...current, basePrice: e.target.value }))} placeholder="Base price" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.currency} onChange={(e) => setPlanForm((current) => ({ ...current, currency: e.target.value }))} placeholder="Currency" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.includedInstructorSeats} onChange={(e) => setPlanForm((current) => ({ ...current, includedInstructorSeats: e.target.value }))} placeholder="Included instructor seats" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.includedContentPackIdsText} onChange={(e) => setPlanForm((current) => ({ ...current, includedContentPackIdsText: e.target.value }))} placeholder="Included pack IDs" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={planForm.addOnEligibleContentPackIdsText} onChange={(e) => setPlanForm((current) => ({ ...current, addOnEligibleContentPackIdsText: e.target.value }))} placeholder="Add-on eligible pack IDs" className="rounded border border-slate-200 px-3 py-2 text-sm md:col-span-2" />
            </div>
            {editable ? (
              <button onClick={handleCreatePlan} className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                Create Plan
              </button>
            ) : null}

            <div className="mt-6 space-y-3">
              {(plans || []).filter(Boolean).map((plan) => (
                <div key={plan.subscriptionPlanId} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-950">{plan.name}</p>
                      <p className="text-sm text-slate-600">{plan.code} · {plan.tier} · {plan.billingInterval}</p>
                    </div>
                    <button
                      onClick={() => handleUpdatePlanActive(plan.subscriptionPlanId, !plan.active)}
                      className="rounded border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      {plan.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    Includes {(plan.includedContentPackIds || []).length} pack(s) and {plan.includedInstructorSeats} instructor seat(s).
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Organization Subscriptions</h2>
            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-4">
              <p className="text-sm font-semibold text-slate-900">Pending Organization Requests</p>
              {pendingCommercialRequests.length ? (
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {pendingCommercialRequests.map((organization) => (
                    <div key={organization.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                      <div className="font-medium text-slate-900">{organization.name || organization.id}</div>
                      <div>Request type: {(organization.commercialRequestType || 'unknown').replace(/_/g, ' ')}</div>
                      {organization.commercialRequestContentPackId ? (
                        <div>Requested pack: {organization.commercialRequestContentPackId}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-600">No pending organization upgrade or trial requests right now.</p>
              )}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <select value={subscriptionForm.organizationId} onChange={(e) => setSubscriptionForm((current) => ({ ...current, organizationId: e.target.value }))} className="rounded border border-slate-200 px-3 py-2 text-sm">
                <option value="">Choose organization</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>{organization.name || organization.id}</option>
                ))}
              </select>
              <select value={subscriptionForm.planCode} onChange={(e) => setSubscriptionForm((current) => ({ ...current, planCode: e.target.value }))} className="rounded border border-slate-200 px-3 py-2 text-sm">
                <option value="">Choose plan</option>
                {plans.map((plan) => (
                  <option key={plan.subscriptionPlanId} value={plan.code}>{plan.name}</option>
                ))}
              </select>
              <select value={subscriptionForm.billingInterval} onChange={(e) => setSubscriptionForm((current) => ({ ...current, billingInterval: e.target.value }))} className="rounded border border-slate-200 px-3 py-2 text-sm">
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="none">None</option>
              </select>
              <input value={subscriptionForm.seatCount} onChange={(e) => setSubscriptionForm((current) => ({ ...current, seatCount: e.target.value }))} placeholder="Seat count" className="rounded border border-slate-200 px-3 py-2 text-sm" />
              <input value={subscriptionForm.instructorSeatLimit} onChange={(e) => setSubscriptionForm((current) => ({ ...current, instructorSeatLimit: e.target.value }))} placeholder="Instructor seat limit" className="rounded border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={handleAssignPlan} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Assign Plan</button>
              <button onClick={() => handleStartTrial(subscriptionForm.organizationId, subscriptionForm.planCode)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Start Trial</button>
            </div>

            <div className="mt-6 space-y-4">
              {organizationCommercialRows.map(({ organization, commercialState }) => {
                const safeCommercialState = commercialState || {};

                return (
                <div key={organization.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-semibold text-slate-950">{organization.name || organization.id}</p>
                      <p className="text-sm text-slate-600">
                        Plan: {safeCommercialState.currentPlan?.name || 'No plan'} · Status: {safeCommercialState.subscription?.status || 'inactive'}
                      </p>
                      <p className="text-sm text-slate-600">
                        Seats: {safeCommercialState.seatCount || 0} / {safeCommercialState.instructorSeatLimit || 'unlimited'}
                      </p>
                    </div>
                    {safeCommercialState.subscription ? (
                      <button
                        onClick={() => handleCancelSubscription(organization.id)}
                        className="rounded border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                      >
                        Cancel Subscription
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-3 text-sm text-slate-600">
                    Included packs: {(safeCommercialState.includedPacks || []).map((pack) => pack.name).join(', ') || 'None'}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Add-on or manually granted packs: {(safeCommercialState.addOnPacks || []).map((pack) => pack.name).join(', ') || 'None'}
                  </div>
                </div>
              );})}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default PlatformAdminBillingPage;
